import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import CountUp from 'react-countup';
import PropTypes from 'prop-types';
import { themeGet } from '@styled-system/theme-get';

import RaffleProgressBarComponent from './RaffleProgressBarComponent.jsx';
import SuggestTileButton from './SuggestTileButton.jsx';
import PrizeStar from '../../../assets/PrizeStar.svg';
import Text from '../../Text';

const TileProgressBarComponent = ({
  organization,
  completedTilesPercentage,
  userData,
  progressBarData,
  demo,
  canSuggestTile,
}) => {
  return (
    <>
      <ProgressAndPoints>
        <ProgressLabel>{organization.tilesWording}</ProgressLabel>
        <ProgressBar hasRaffle={progressBarData.hasRaffle}>
          <EmptyProgressBar hasRaffle={progressBarData.hasRaffle}>
            <CompletedTiles
              color={progressBarData.color}
              width={`${completedTilesPercentage}%`}
            />
          </EmptyProgressBar>
        </ProgressBar>
        <TilesTotal
          bg="white"
          color={progressBarData.color}
          mr="auto"
          isGuestUser={userData.isGuestUser}
        >
          {progressBarData.completedTiles}/{progressBarData.incompletedTiles}
        </TilesTotal>
        <PointsLabel>{demo.pointsWording}</PointsLabel>
        <PrizeStartWrapper>
          <PrizeStarImg src={PrizeStar} />
          <PointsTotal
            as="div"
            variant="subtitle2"
            color={progressBarData.color}
          >
            <CountUp
              start={progressBarData.startingPoints}
              end={progressBarData.points}
              duration={2.75}
            />
          </PointsTotal>
        </PrizeStartWrapper>
      </ProgressAndPoints>
      {progressBarData.hasRaffle && (
        <RaffleProgressBarComponent
          progressBarData={progressBarData}
          percentage={progressBarData.raffleBarCompletion}
        />
      )}
      {canSuggestTile && <SuggestTileButton color={progressBarData.color} />}
    </>
  );
};

TileProgressBarComponent.propTypes = {
  organization: PropTypes.object,
  completedTilesPercentage: PropTypes.number,
  userData: PropTypes.object,
  progressBarData: PropTypes.object,
  demo: PropTypes.object,
  canSuggestTile: PropTypes.bool,
};

export default TileProgressBarComponent;

const ProgressAndPoints = styled.div`
  display: flex;
  width: 100%;
  max-width: 735px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0 13px;
  ${themeGet('mediaQueries.forMediumUp')} {
    flex-wrap: nowrap;
    padding: 0;
  }
`;

const PrizeStartWrapper = styled.div`
  display: flex;
`;
const ProgressLabel = styled(Text)`
  color: ${themeGet('colors.black.100')};
  background: ${themeGet('colors.white')};
  text-transform: capitalize;
  ${themeGet('mediaQueries.forMediumUp')} {
    padding: 0 16px;
    margin-left: 24px;
    width: fit-content;
    height: 34px;
    align-items: center;
    display: flex;
    border-radius: 20px 0px 0px 20px;
    position: relative;
    &:after {
      content: '';
      border-right: 1px solid ${themeGet('colors.black.10')};
      position: absolute;
      top: 8px;
      bottom: 8px;
      right: 0px;
    }
  }
`;

ProgressLabel.defaultProps = {
  variant: 'subtitle2',
};

const PointsLabel = styled(ProgressLabel)`
  display: none;
  ${themeGet('mediaQueries.forMediumUp')} {
    display: flex;
  }
`;

const PrizeStarImg = styled.img`
  padding: 0 3px 0 8px;
  ${themeGet('mediaQueries.forMediumUp')} {
    display: none;
  }
`;

const TilesTotal = styled(Text)`
  padding: ${props => (props.isGuestUser ? '0 7px' : '0 6px')};
  border-radius: 0 20px 20px 0;
  ${themeGet('mediaQueries.forMediumUp')} {
    padding: 8px 16px;
    font-size: 18px;
  }
`;

const ProgressBar = styled.div`
  overflow: hidden;
  font-size: 18px;
  border-radius: 0;
  margin: 0;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: ${({ hasRaffle }) => (hasRaffle ? '70px' : '157px')};
  background: ${themeGet('colors.white')};
  order: 2;
  ${themeGet('mediaQueries.forMediumUp')} {
    order: 0;
    height: 2.2rem;
    padding: 0 0 0 16px;
    max-width: 462px;
  }
`;

const EmptyProgressBar = styled.div`
  height: 12px;
  border-radius: 20px;
  background-color: ${themeGet('colors.black.10')};
  width: 100%;
  min-width: ${({ hasRaffle }) => (hasRaffle ? '70px' : '157px')};
  ${themeGet('mediaQueries.forMediumUp')} {
    margin: 0;
  }
`;

const allTilesCompleted = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const CompletedTiles = styled.div`
  height: 12px;
  text-align: right;
  border-radius: 20px;
  background: ${props => props.color};
  transition: width 1s ease-in-out;
  width: ${({ width }) => width};
  ${({ width }) =>
    width === '100%' &&
    css`
      animation: ${allTilesCompleted} 2s;
    `};
`;

const PointsTotal = styled(Text)`
  text-align: center;
  padding: 10px 0;
  background: ${themeGet('colors.white')};
  border-radius: 0 20px 20px 0;
  ${themeGet('mediaQueries.forMediumUp')} {
    font-size: 18px;
    padding: 8px 16px 8px 13px;
  }
`;
